/**
 * Event Formatters
 * Transforms API event data to FullCalendar format
 */

import { uuidv4 } from 'common/uuidGenerator';
import moment from 'moment';

/**
 * Format a single event for FullCalendar
 *
 * @param {object} apiEvent - Event from API
 * @param {string} timezone - User timezone
 * @param {string} startDate - Start date of the view range
 * @param {string} endDate - End date of the view range
 * @returns {object} FullCalendar event object
 */
export const formatEventForCalendar = (apiEvent, timezone, startDate, endDate) => {
  const classNames = [];

  // Add firm-wide indicator
  if (apiEvent.is_firm_wide_event) {
    classNames.push('firm-wide-event');
  }

  // Add recurring indicator
  if (apiEvent.is_recurring_event) {
    classNames.push('recurring-event');
  }

  let eventStart = apiEvent.event_start;
  let eventEnd = apiEvent.event_end;

  if (apiEvent.calendar_type === 'case') {
    // Parse statute of limitations date and create event in user's timezone
    const solMoment = moment(apiEvent.statute_of_limitations);

    eventStart = moment
      .tz(
        {
          year: solMoment.year(),
          month: solMoment.month(),
          date: solMoment.date(),
          hour: 10,
          minute: 0,
          second: 0,
          millisecond: 0,
        },
        timezone
      )
      .toDate();

    eventEnd = moment
      .tz(
        {
          year: solMoment.year(),
          month: solMoment.month(),
          date: solMoment.date(),
          hour: 10,
          minute: 30,
          second: 0,
          millisecond: 0,
        },
        timezone
      )
      .toDate();
  } else if (apiEvent.calendar_type === 'task') {
    // Parse task due date and create event in user's timezone
    const dueDateMoment = moment.tz(apiEvent.due_date, timezone);

    eventStart = moment
      .tz(
        {
          year: dueDateMoment.year(),
          month: dueDateMoment.month(),
          date: dueDateMoment.date(),
          hour: 9,
          minute: 0,
          second: 0,
          millisecond: 0,
        },
        timezone
      )
      .toDate();

    eventEnd = moment
      .tz(
        {
          year: dueDateMoment.year(),
          month: dueDateMoment.month(),
          date: dueDateMoment.date(),
          hour: 9,
          minute: 30,
          second: 0,
          millisecond: 0,
        },
        timezone
      )
      .toDate();
  } else if (apiEvent.calendar_type === 'user') {
    // Parse date-only string and convert to appropriate year for birthday display
    const dobMoment = moment(apiEvent.dob, 'YYYY-MM-DD'); // Parse as date-only, no timezone needed
    const startYear = moment(startDate).year();
    const endYear = moment(endDate).year();

    // Determine which year to use for the birthday
    // Create a test date in the start year
    const birthdayInStartYear = moment.tz(
      {
        year: startYear,
        month: dobMoment.month(),
        date: dobMoment.date(),
      },
      timezone
    );

    // Check if birthday falls within the view range in the start year
    const rangeStart = moment.tz(startDate, timezone);

    let targetYear = startYear;

    // If the birthday in start year is before the range start, and we're viewing multiple years, use end year
    if (birthdayInStartYear.isBefore(rangeStart) && startYear !== endYear) {
      targetYear = endYear;
    }

    // Create birthday event for the determined year in user's timezone
    eventStart = moment
      .tz(
        {
          year: targetYear,
          month: dobMoment.month(),
          date: dobMoment.date(),
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        },
        timezone
      )
      .toDate();

    eventEnd = moment
      .tz(
        {
          year: targetYear,
          month: dobMoment.month(),
          date: dobMoment.date(),
          hour: 0,
          minute: 30,
          second: 0,
          millisecond: 0,
        },
        timezone
      )
      .toDate();
  }

  if (apiEvent.all_day && eventStart !== eventEnd) {
    eventEnd = moment(eventEnd).add(1, 'day').format('YYYY-MM-DD');
  }
  const backgroundColor = apiEvent?.is_google_event
    ? '#4285F4'
    : apiEvent.calendar_type === 'event'
    ? apiEvent.calendar_color || '#3b82f6'
    : '#F0F2F5';

  return {
    id: apiEvent.event_id || apiEvent?.task_id,
    title: apiEvent.event_title || apiEvent.title || apiEvent.task_name,
    start: eventStart,
    end: eventEnd,
    backgroundColor: backgroundColor,
    borderColor: backgroundColor,
    allDay: apiEvent.all_day || false,
    classNames,
    display: 'block', // Ensure all-day events are displayed as blocks for month view styling
    extendedProps: {
      id: apiEvent.event_id || apiEvent?.task_id,
      sk: apiEvent.sk,
      pk: apiEvent.pk,
      calendar_type: apiEvent.calendar_type,
      eventType: apiEvent.event_type,
      location: apiEvent._location_,
      description: apiEvent.description,
      attendees: apiEvent.attendees,
      isFirmWide: apiEvent.is_firm_wide_event || false,
      isRecurring: apiEvent.is_recurring_event || false,
      practiceArea: apiEvent._practice_area_name_,
      caseId: apiEvent.case_id,
      subtitle: apiEvent.subtitle,
      room: apiEvent.room,
      originalData: apiEvent, // Keep reference to original data
      case_name: apiEvent.case_name,
      participants: apiEvent.participants,
      task_id: apiEvent.task_id,
      due_date: apiEvent.due_date,
      event_type: apiEvent.event_type?._event_type_,
      event_type_icon: apiEvent.event_type?._event_type_icon_,
      contact_id: apiEvent.contact_id,
      case_id: apiEvent.case_id,
      contact_name: apiEvent?.first_name + ' ' + apiEvent?.last_name,
      is_google_event: apiEvent.is_google_event || false,
    },
  };
};

/**
 * Create a "x more" placeholder event for grouped views
 *
 * @param {string} date - Date (YYYY-MM-DD)
 * @param {number} count - Number of additional events
 * @returns {object} FullCalendar event object for "more" link
 */
export const createMoreEventsPlaceholder = (date, count) => {
  return {
    id: `more-${uuidv4()}-${count}`,
    title: `+${count} more`,
    start: date,
    display: 'background',
    backgroundColor: 'transparent',
    classNames: ['more-events-link', 'custom-more-link'],
    allDay: true,
    // display: 'block',
    extendedProps: {
      isMoreLink: true,
      moreCount: count,
      date: date,
    },
  };
};

/**
 * Transform grouped events from API to FullCalendar format
 * Used for month/week views with "x more" functionality
 *
 * @param {object} apiData - Grouped events by date from API
 * @param {string} timezone - User timezone
 * @param {string} startDate - Start date of the view range
 * @param {string} endDate - End date of the view range
 * @returns {array} Array of FullCalendar event objects
 */
export const transformGroupedEvents = (apiData, timezone, startDate, endDate) => {
  const events = [];

  if (!apiData || typeof apiData !== 'object') {
    return events;
  }

  // Iterate through each date
  Object.keys(apiData).forEach((date) => {
    const dayData = apiData[date];
    if (!dayData) {
      return;
    }

    // Add the visible events (max 2) for grouped views
    dayData?.events?.forEach((apiEvent) => {
      events.push(formatEventForCalendar(apiEvent, timezone, startDate, endDate));
    });

    if (dayData.more_events_count && dayData.more_events_count > 0) {
      events.push(createMoreEventsPlaceholder(moment.tz(date, timezone).format(), dayData.more_events_count));
    }
  });

  return events;
};

/**
 * Transform year view events from API to FullCalendar format
 * Year view returns simple date:count object
 *
 * @param {object} apiData - Date:count object from API {  "2024-02-10": 1, "2024-03-12": 4, ... }
 * @returns {array} Array of FullCalendar event count indicators
 */
export const transformYearEvents = (apiData, timezone) => {
  const events = [];

  if (!apiData || typeof apiData !== 'object') {
    return events;
  }

  // Iterate through each date and create count indicators
  Object.keys(apiData).forEach((date) => {
    const count = apiData[date];

    // Skip if no events on this date
    if (!count || count === 0) {
      return;
    }

    events.push({
      id: `year-count-${date}`,
      title: `+${count} more`,
      start: moment.tz(date, timezone).format(),
      end: moment.tz(date, timezone).add(1, 'hour').format(),
      display: 'background',
      backgroundColor: 'transparent',
      classNames: ['more-events-link', 'custom-more-link'],
      allDay: true,
      // display: 'block',
      extendedProps: {
        isMoreLink: true,
        moreCount: count,
        date: date,
      },
    });
  });

  return events;
};

/**
 * Transform day events from API to FullCalendar format
 * Used for day view - shows all events, no grouping
 *
 * @param {array} apiData - Day events from API
 * @param {string} timezone - User timezone
 * @param {string} startDate - Start date of the view range
 * @param {string} endDate - End date of the view range
 * @returns {array} Array of FullCalendar event objects
 */
export const transformDayEvents = (apiData, timezone, startDate, endDate) => {
  if (!apiData || !Array.isArray(apiData)) {
    return [];
  }

  return apiData.map((apiEvent) => formatEventForCalendar(apiEvent, timezone, startDate, endDate));
};

/**
 * Get date from event (handles both ISO string and Date object)
 *
 * @param {string|Date} dateValue - Date value
 * @returns {Date} Date object
 */
export const getEventDate = (dateValue) => {
  if (dateValue instanceof Date) {
    return dateValue;
  }
  return new Date(dateValue);
};

/**
 * Check if an event is a "more" link placeholder
 *
 * @param {object} event - FullCalendar event
 * @returns {boolean} True if event is a "more" link
 */
export const isMoreLinkEvent = (event) => {
  return event?.extendedProps?.isMoreLink === true;
};
